/**
 * One poller for every media item the page is waiting on, instead of one interval per item.
 *
 * Each tick asks `check_encoding_list` about the whole pending set in a single request. The
 * server answers with a status per item rather than an error, so an item that failed to encode
 * (or that the encoder never reported back on) drops out of the list instead of being polled
 * until the tab is closed.
 *
 * Stashed on `window` for the reason `shared/globalEventBus` is: app modules are copied into
 * every entry bundle, and two copies would mean two intervals.
 */

import captionPlayerBus from 'shared/captionPlayer';
import addMediaResourcesModuleImport from './add_media_resources';

const GLOBAL_KEY = '__mmMediaEncodingsUpdater__';
const CHECK_INTERVAL = 3000;
const ENCODABLE_TYPES = ['upl_video', 'recorded_audio'];

const addMediaResources = addMediaResourcesModuleImport();

function encodingPartOf (mediaContent) {
    if (!mediaContent || ENCODABLE_TYPES.indexOf(mediaContent.type) === -1) {
        return undefined;
    }
    return mediaContent[mediaContent.type];
}

function applyEncodedMedia (mediaContent, type, data) {
    mediaContent[type] = data;
    if (type === 'upl_video') {
        mediaContent.thumbnail = data.preview_url; // fix for folder item
    }
}

/**
 * Carries a `mediaEncoded` event over to an activity post that holds its own copy of the media
 * item - a diary entry saved before the encoding finished comes back from the server with the
 * placeholder preview and an unencoded item, and the updater only ever touches the original.
 *
 * @param activityPost {Object} serialized with its `items`
 * @param event {Object} the `mediaEncoded` payload
 */
function applyEncodedToActivityPost (activityPost, {type, encoding_id, encoding_media}) {
    const items = activityPost?.items;
    if (!items) {
        return;
    }
    const data = encoding_media[type];
    const isEncoded = media => media?.type === type && media[type]?.encoding_id === encoding_id;
    items.forEach(item => {
        if (isEncoded(item.media_attachment)) {
            applyEncodedMedia(item.media_attachment, type, data);
        }
    });
    // the post's preview is its first media item's, see ActivityPost.get_preview_image_url
    const firstMedia = items.find(item => item.media_attachment)?.media_attachment;
    if (type === 'upl_video' && isEncoded(firstMedia) && data.preview_url) {
        activityPost.preview_url = data.preview_url;
        activityPost.thumbnail_url = data.preview_url;
    }
}

class MediaEncodingsUpdater {
    constructor () {
        this.watched = [];
        this.intervalId = null;
        this.resources = addMediaResources; // swapped out in tests
    }

    /**
     * @param mediaContent {Object} media item, mutated in place once the encoding finishes
     * @returns {Promise<Boolean>} true once encoded, false if it failed or is unknown to the server
     */
    watch (mediaContent) {
        const encodingPart = encodingPartOf(mediaContent);
        if (!encodingPart) {
            return Promise.resolve(false);
        }
        if (encodingPart.encoding_done) {
            return Promise.resolve(true);
        }
        if (encodingPart.encoding_probably_failed) {
            return Promise.resolve(false);
        }

        return new Promise(resolve => {
            const existing = this.watched.find(item => item.mediaContent === mediaContent);
            if (existing) {
                existing.resolvers.push(resolve);
                return;
            }
            this.watched.push({
                mediaContent,
                encoding_id: encodingPart.encoding_id,
                type: mediaContent.type,
                resolvers: [resolve],
            });
            this.$_startPolling();
        });
    }

    unwatch (mediaContent) {
        this.$_settle(item => item.mediaContent === mediaContent, false);
    }

    clear () {
        this.$_settle(() => true, false);
    }

    $_startPolling () {
        if (this.intervalId === null) {
            this.intervalId = setInterval(() => this.$_check(), CHECK_INTERVAL);
        }
    }

    $_stopPolling () {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /** Drops every matching item off the list, resolving whoever was waiting for it. */
    $_settle (matches, encoded) {
        const settled = this.watched.filter(matches);
        this.watched = this.watched.filter(item => settled.indexOf(item) === -1);
        settled.forEach(item => item.resolvers.forEach(resolve => resolve(encoded)));
        if (!this.watched.length) {
            this.$_stopPolling();
        }
    }

    $_check () {
        if (!this.watched.length) {
            this.$_stopPolling();
            return;
        }
        const encodings = this.watched.map(({encoding_id, type}) => ({encoding_id, type}));
        return this.resources.check_encoding_list(encodings).then(res => {
            res.data.results.forEach(result => {
                const matches = item => item.encoding_id === result.encoding_id && item.type === result.type;
                if (result.status === 'complete') {
                    this.watched.filter(matches).forEach(item => this.$_applyEncoded(item, result.data));
                    this.$_settle(matches, true);
                } else if (result.status !== 'processing') { // failed, not_found or invalid - nothing left to wait for
                    this.watched.filter(matches).forEach(item => this.$_markFailed(item));
                    this.$_settle(matches, false);
                }
            });
        }, () => {
            // a failed request is just a missed tick - the next one asks again
        });
    }

    $_applyEncoded (item, data) {
        applyEncodedMedia(item.mediaContent, item.type, data);
        captionPlayerBus.emit('mediaEncoded', {
            type: item.type,
            encoding_id: item.encoding_id,
            encoding_media: item.mediaContent,
        });
    }

    $_markFailed (item) {
        const encodingPart = encodingPartOf(item.mediaContent);
        if (encodingPart) {
            encodingPart.encoding_probably_failed = true;
        }
    }
}

const mediaEncodingsUpdater = window[GLOBAL_KEY] || (window[GLOBAL_KEY] = new MediaEncodingsUpdater());

export default mediaEncodingsUpdater;
export {mediaEncodingsUpdater, MediaEncodingsUpdater, CHECK_INTERVAL, applyEncodedToActivityPost};
